// Month grid for DatePicker, built on the ARIA date-picker-dialog pattern:
// a labelled grid, one tab stop (roving focus), and arrow/page keys to move
// between days.
//
// Month and year dropdowns are the point of the thing. A business founded in
// 1998 is ~330 clicks away with month arrows alone, which is why operators
// gave up on the calendar and typed the date instead.

import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { isValidDate } from "@/lib/formatters";

const WEEKDAYS = [
  { short: "Su", long: "Sunday" },
  { short: "Mo", long: "Monday" },
  { short: "Tu", long: "Tuesday" },
  { short: "We", long: "Wednesday" },
  { short: "Th", long: "Thursday" },
  { short: "Fr", long: "Friday" },
  { short: "Sa", long: "Saturday" },
] as const;

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function parseIsoDate(iso: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  const [, y, mo, d] = m.map(Number);
  const date = new Date(y, mo - 1, d);
  if (
    date.getFullYear() !== y ||
    date.getMonth() !== mo - 1 ||
    date.getDate() !== d
  ) {
    return null;
  }
  return date;
}

export function mdyToDate(mdy: string): Date | null {
  if (!isValidDate(mdy)) return null;
  const [m, d, y] = mdy.split("/").map(Number);
  return new Date(y, m - 1, d);
}

export function formatMdy(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${mm}/${dd}/${d.getFullYear()}`;
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

function addMonths(d: Date, n: number): Date {
  const anchor = new Date(d.getFullYear(), d.getMonth() + n, 1);
  const year = anchor.getFullYear();
  const month = anchor.getMonth();
  // Jan 31 + 1 month is Feb 28/29, not Mar 3.
  return new Date(year, month, Math.min(d.getDate(), daysInMonth(year, month)));
}

function clamp(d: Date, min: Date | null, max: Date | null): Date {
  if (min && d < min) return min;
  if (max && d > max) return max;
  return d;
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

interface Props {
  /** Selected date as MM/DD/YYYY, or "". A string (not a Date) so the
   *  calendar's own month navigation isn't reset by every parent render. */
  selected: string;
  /** Inclusive bounds, YYYY-MM-DD. Navigation stops at them: a month the
   *  operator can't pick a day in is a month they shouldn't be browsing. */
  minDate?: string;
  maxDate?: string;
  onSelect: (mdy: string) => void;
  onClear: () => void;
}

export function DatePickerCalendar({
  selected,
  minDate,
  maxDate,
  onSelect,
  onClear,
}: Props) {
  const min = useMemo(
    () => (minDate ? parseIsoDate(minDate) : null),
    [minDate],
  );
  const max = useMemo(
    () => (maxDate ? parseIsoDate(maxDate) : null),
    [maxDate],
  );
  const today = useMemo(() => startOfDay(new Date()), []);
  const selectedDate = useMemo(() => mdyToDate(selected), [selected]);

  // The focused day. Doubles as the displayed month, so there's no separate
  // view state to drift out of sync with it.
  const [active, setActive] = useState<Date>(() =>
    clamp(selectedDate ?? today, min, max),
  );

  // Move focus only for keyboard navigation and the initial open — not when
  // the operator is typing in the text field or picking from a dropdown.
  const shouldFocusDay = useRef(true);
  const activeDayRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!shouldFocusDay.current) return;
    shouldFocusDay.current = false;
    activeDayRef.current?.focus();
  });

  // Follow the text input while the popover is open.
  useEffect(() => {
    const next = mdyToDate(selected);
    if (next) setActive(clamp(next, min, max));
  }, [selected, min, max]);

  const year = active.getFullYear();
  const month = active.getMonth();

  const weeks = useMemo(() => {
    const leading = new Date(year, month, 1).getDay();
    const total = daysInMonth(year, month);
    const cells: (Date | null)[] = Array(leading).fill(null);
    for (let day = 1; day <= total; day++) {
      cells.push(new Date(year, month, day));
    }
    while (cells.length % 7 !== 0) cells.push(null);
    const rows: (Date | null)[][] = [];
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
    return rows;
  }, [year, month]);

  const years = useMemo(() => {
    const first = min ? min.getFullYear() : today.getFullYear() - 120;
    const last = max ? max.getFullYear() : today.getFullYear() + 10;
    const out: number[] = [];
    // Newest first: recent years are the common case for both business
    // start dates and the year dropdown's type-ahead.
    for (let y = last; y >= first; y--) out.push(y);
    return out;
  }, [min, max, today]);

  function outOfRange(d: Date): boolean {
    if (min && d < min) return true;
    if (max && d > max) return true;
    return false;
  }

  /** True when no day in the month is selectable. */
  function monthOutOfRange(y: number, m: number): boolean {
    const first = new Date(y, m, 1);
    const last = new Date(y, m, daysInMonth(y, m));
    return (min != null && last < min) || (max != null && first > max);
  }

  function goTo(next: Date, viaKeyboard: boolean) {
    if (viaKeyboard) shouldFocusDay.current = true;
    setActive(clamp(next, min, max));
  }

  const prevDisabled = monthOutOfRange(
    month === 0 ? year - 1 : year,
    month === 0 ? 11 : month - 1,
  );
  const nextDisabled = monthOutOfRange(
    month === 11 ? year + 1 : year,
    month === 11 ? 0 : month + 1,
  );

  function onGridKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    let next: Date | null = null;
    switch (e.key) {
      case "ArrowLeft":
        next = addDays(active, -1);
        break;
      case "ArrowRight":
        next = addDays(active, 1);
        break;
      case "ArrowUp":
        next = addDays(active, -7);
        break;
      case "ArrowDown":
        next = addDays(active, 7);
        break;
      case "Home":
        next = addDays(active, -active.getDay());
        break;
      case "End":
        next = addDays(active, 6 - active.getDay());
        break;
      case "PageUp":
        next = addMonths(active, e.shiftKey ? -12 : -1);
        break;
      case "PageDown":
        next = addMonths(active, e.shiftKey ? 12 : 1);
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        if (!outOfRange(active)) onSelect(formatMdy(active));
        return;
      default:
        return;
    }
    e.preventDefault();
    goTo(next, true);
  }

  const gridLabel = `${MONTHS[month]} ${year}`;

  return (
    <div className="w-[17.5rem] select-none rounded-xl border border-brand-border bg-brand-surface-2 p-3 shadow-pop">
      <div className="mb-2 flex items-center gap-1">
        <button
          type="button"
          onClick={() => goTo(addMonths(active, -1), false)}
          disabled={prevDisabled}
          aria-label="Previous month"
          className="rounded-md p-1.5 text-brand-muted transition-colors hover:bg-brand-border/50 hover:text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-blue/40 disabled:pointer-events-none disabled:opacity-30"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        </button>

        <div className="flex flex-1 items-center justify-center gap-1">
          <select
            value={month}
            onChange={(e) =>
              goTo(new Date(year, Number(e.target.value), 1), false)
            }
            aria-label="Month"
            className="rounded-md border border-transparent bg-transparent px-1 py-1 text-sm font-medium text-brand-text hover:border-brand-border focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
          >
            {MONTHS.map((name, index) => (
              <option
                key={name}
                value={index}
                disabled={monthOutOfRange(year, index)}
                className="bg-brand-surface"
              >
                {name}
              </option>
            ))}
          </select>
          <select
            value={year}
            onChange={(e) =>
              goTo(new Date(Number(e.target.value), month, 1), false)
            }
            aria-label="Year"
            className="rounded-md border border-transparent bg-transparent px-1 py-1 text-sm font-medium text-brand-text hover:border-brand-border focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
          >
            {years.map((y) => (
              <option key={y} value={y} className="bg-brand-surface">
                {y}
              </option>
            ))}
          </select>
        </div>

        <button
          type="button"
          onClick={() => goTo(addMonths(active, 1), false)}
          disabled={nextDisabled}
          aria-label="Next month"
          className="rounded-md p-1.5 text-brand-muted transition-colors hover:bg-brand-border/50 hover:text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-blue/40 disabled:pointer-events-none disabled:opacity-30"
        >
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      <div role="grid" aria-label={gridLabel} onKeyDown={onGridKeyDown}>
        <div role="row" className="grid grid-cols-7 gap-0.5">
          {WEEKDAYS.map((d) => (
            <abbr
              key={d.short}
              role="columnheader"
              aria-label={d.long}
              title={d.long}
              className="py-1 text-center text-[11px] font-medium uppercase tracking-wide text-brand-muted no-underline"
            >
              {d.short}
            </abbr>
          ))}
        </div>

        {weeks.map((week, i) => (
          <div key={i} role="row" className="grid grid-cols-7 gap-0.5">
            {week.map((day, j) => {
              if (!day) {
                return <div key={`pad-${j}`} role="gridcell" aria-hidden="true" />;
              }
              const disabled = outOfRange(day);
              const isActive = sameDay(day, active);
              const isSelected = selectedDate
                ? sameDay(day, selectedDate)
                : false;
              const isToday = sameDay(day, today);

              return (
                <button
                  key={day.getDate()}
                  ref={isActive ? activeDayRef : undefined}
                  type="button"
                  role="gridcell"
                  // One tab stop for the whole grid; arrows move within it.
                  tabIndex={isActive ? 0 : -1}
                  disabled={disabled}
                  aria-selected={isSelected}
                  aria-current={isToday ? "date" : undefined}
                  aria-label={`${MONTHS[day.getMonth()]} ${day.getDate()}, ${day.getFullYear()}`}
                  onClick={() => onSelect(formatMdy(day))}
                  className={[
                    "h-8 rounded-md text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-brand-blue/50",
                    disabled
                      ? "cursor-not-allowed text-brand-muted/30"
                      : "text-brand-text hover:bg-brand-border/60",
                    isSelected
                      ? "bg-brand-blue font-semibold text-white hover:bg-brand-blue/90"
                      : "",
                    isToday && !isSelected
                      ? "font-semibold text-brand-blue ring-1 ring-inset ring-brand-blue/50"
                      : "",
                  ].join(" ")}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      <div className="mt-2 flex items-center justify-between border-t border-brand-border pt-2">
        <button
          type="button"
          onClick={() => onSelect(formatMdy(today))}
          // Hidden bound, not a broken button: on a date-of-birth field the
          // 18-year rule puts today out of range, so there's nothing to pick.
          disabled={outOfRange(today)}
          className="rounded-md px-2 py-1 text-xs font-medium text-brand-blue transition-colors hover:bg-brand-blue/10 focus:outline-none focus:ring-2 focus:ring-brand-blue/40 disabled:pointer-events-none disabled:opacity-40"
        >
          Today
        </button>
        <button
          type="button"
          onClick={onClear}
          disabled={!selected}
          className="rounded-md px-2 py-1 text-xs text-brand-muted transition-colors hover:bg-brand-border/50 hover:text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-blue/40 disabled:pointer-events-none disabled:opacity-40"
        >
          Clear
        </button>
      </div>
    </div>
  );
}
